import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, differenceInDays } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import {
  Router as RouterIcon, Zap, Hash, Wifi, Calendar, Gauge, ChevronRight, RefreshCw,
  CreditCard, SlidersHorizontal, Gift, FileText, Settings,
} from 'lucide-react';
import { customerAPI } from '../../services/api';
import SupportQuickAction from './SupportQuickAction';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtBytes = bytes => {
  const gb = (bytes || 0) / 1e9;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${((bytes || 0) / 1e6).toFixed(0)} MB`;
};
const fmtSpeed = v => (v >= 10 ? v.toFixed(0) : (v || 0).toFixed(1));

const QUICK_ACTIONS = [
  { icon: CreditCard, label: 'Recharge', to: '/portal/payments', color: '#2563eb' },
  { icon: SlidersHorizontal, label: 'Change Plan', to: '/portal/plans?service=broadband', color: '#7c3aed' },
  { icon: FileText, label: 'View Invoice', to: '/portal/payments', color: '#0d9488' },
  { icon: Settings, label: 'Wi-Fi Settings', to: '/portal/wifi', color: '#0891b2' },
  { icon: Gift, label: 'Refer & Earn', to: '/portal/refer', color: '#d97706' },
];

// Card for a broadband service — connection ID, a Plan/Valid-Till/Status stat row,
// live down/up numbers with a small history graph, usage-stat tiles, router status
// and its own Quick Actions row. Sourced via the self-scoped /customers/me/* endpoints.
export default function BroadbandHero({ data, kind = 'broadband', connectionId, durationType, status }) {
  const [rate, setRate] = useState(null);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [device, setDevice] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data: r } = await customerAPI.getMyBandwidth();
        if (cancelled || !r.data) return;
        setRate(r.data);
        if (r.data.online) {
          const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          setHistory(h => {
            const next = [...h, { time, rx: r.data.rx, tx: r.data.tx }];
            return next.length > 30 ? next.slice(-30) : next;
          });
        }
      } catch { /* transient — keep last known rate */ }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    customerAPI.getMyUsage().then(({ data }) => { if (!cancelled) setUsage(data.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Router status polls independently (each call also nudges the ONT via TR-069
  // connection request server-side — see customers.js /me/device-status) so an
  // already-active router shows "Online" within a poll or two instead of only
  // after the ONT's next 5-minute periodic inform / a full page reload.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await customerAPI.getMyDeviceStatus();
        if (!cancelled) setDevice(data.data);
      } catch { /* transient — keep last known status */ }
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const plan = data.plan;
  const online = rate?.online;
  const daysLeft = data.expiryDate ? differenceInDays(new Date(data.expiryDate), new Date()) : null;
  const expired = daysLeft !== null && daysLeft < 0;
  // Regular (durationType 'regular'/unset) customers are renewed automatically by the
  // nightly cron, so unlike DTD there's no urgency-driven window — the button is just
  // always offered as an optional way to prepay ahead of time and lock in the advance
  // discount tiers (2026-08-01, see CustomerRenewPage.js).
  const isDtd = durationType === 'dtd';
  const showRenew = isDtd ? (expired || (daysLeft !== null && daysLeft <= 5)) : true;
  // See CatvHero for why: the per-service `isActive` flag defaults false and only
  // flips true via a renewal payment or approved reconnection — account-level
  // `status` (what staff actually toggle) is the reliable signal for this badge.
  const isActive = status ? status === 'active' : data.isActive !== false;

  return (
    <div style={{ marginBottom: 20 }}>
      <div className="card" style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 800 }}>Broadband Connection</div>
            {(data.username || connectionId) && (
              <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                Connection ID: {data.username || connectionId}
              </div>
            )}
          </div>
          <span className={`badge badge-${online || isActive ? 'success' : 'danger'}`} style={{ flexShrink: 0 }}>
            {online ? 'Online' : (isActive ? 'Active' : 'Inactive')}
          </span>
        </div>

        {/* Plan / Valid Till / Status stat row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
          <div>
            <div style={{
              width: 30, height: 30, borderRadius: 9, background: 'var(--primary-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
            }}>
              <Wifi size={15} color="var(--primary)" />
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Plan</div>
            <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{plan?.displayName || plan?.name || '—'}</div>
            {plan?.speed && <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{plan.speed.download} Mbps</div>}
          </div>
          <div>
            <div style={{
              width: 30, height: 30, borderRadius: 9, background: '#ede9fe',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
            }}>
              <Calendar size={15} color="#7c3aed" />
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Valid Till</div>
            <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{data.expiryDate ? format(new Date(data.expiryDate), 'dd MMM yyyy') : '—'}</div>
            {daysLeft !== null && (
              <div style={{ fontSize: '.7rem', color: expired ? '#dc2626' : 'var(--text-muted)' }}>
                {expired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft} Days Left`}
              </div>
            )}
          </div>
          <div>
            <div style={{
              width: 30, height: 30, borderRadius: 9, background: online ? '#dcfce7' : '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6,
            }}>
              <Gauge size={15} color={online ? '#16a34a' : '#dc2626'} />
            </div>
            <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>Status</div>
            <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{rate ? (online ? 'Connected' : 'Offline') : 'Checking…'}</div>
          </div>
        </div>

        {data.username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: '.8rem', color: 'var(--text-muted)' }}>
            <Hash size={12} />
            PPPoE:
            <span className="font-mono" style={{ color: 'var(--text)', fontWeight: 700, wordBreak: 'break-all' }}>{data.username}</span>
          </div>
        )}

        {/* Live speed — big numbers over a small history graph */}
        <div style={{ marginTop: 16, height: 100 }}>
          <div style={{ position: 'relative', height: '100%' }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={history} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="heroRx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="heroTx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="time" hide />
                  <YAxis hide domain={[0, 'auto']} />
                  <Area type="monotone" dataKey="rx" stroke="#0ea5e9" strokeWidth={1.5} fill="url(#heroRx)" dot={false} isAnimationActive={false} />
                  <Area type="monotone" dataKey="tx" stroke="#f97316" strokeWidth={1.5} fill="url(#heroTx)" dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={{ position: 'relative', display: 'flex', gap: 28, paddingTop: 8 }}>
              <div>
                <div style={{ fontSize: '.66rem', fontWeight: 700, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '.05em' }}>↓ Download</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtSpeed(rate?.rx || 0)}</span>
                  <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '.66rem', fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: '.05em' }}>↑ Upload</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{fmtSpeed(rate?.tx || 0)}</span>
                  <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showRenew && (
          <Link to={`/portal/renew?service=${kind}`} className="btn btn-primary" style={{
            marginTop: 14, width: '100%', justifyContent: 'center', padding: '11px 0',
          }}>
            {isDtd ? 'Renew Now' : 'Recharge in Advance'}
          </Link>
        )}
        {!isDtd && (
          <div style={{ marginTop: showRenew ? 8 : 14, padding: '10px 14px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: '.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            Auto-renews monthly — no action needed{!expired ? ', recharge above only to lock in advance savings' : ''}.
          </div>
        )}
      </div>

      {/* Usage tiles — live bandwidth sits alongside Today/Week/Month so the customer
          sees "how much I've used" and "what my connection is doing right now" together.
          The whole section links to the full Usage Details page (live speed, All-Time
          total, session history). */}
      {usage && (
        <Link to="/portal/usage" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 8 }}>
            <span style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              Usage Stats
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: '.74rem', fontWeight: 600, color: 'var(--primary)' }}>
              View Details <ChevronRight size={13} />
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: online ? '#dcfce718' : '#f3f4f6',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: online ? '#22c55e' : '#ef4444' }} />
                </div>
                <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>Live Now</span>
              </div>
              <div style={{ display: 'flex', gap: 10, fontVariantNumeric: 'tabular-nums' }}>
                <span style={{ fontSize: '.95rem', fontWeight: 800, color: '#0284c7' }}>↓{fmtSpeed(rate?.rx || 0)}</span>
                <span style={{ fontSize: '.95rem', fontWeight: 800, color: '#c2410c' }}>↑{fmtSpeed(rate?.tx || 0)}</span>
                <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', alignSelf: 'center' }}>Mbps</span>
              </div>
            </div>
            {[['Today', usage.today, '#0ea5e9'], ['This Week', usage.week, '#8b5cf6'], ['This Month', usage.month, '#f97316']].map(([label, u, color]) => (
              <div key={label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Zap size={13} color={color} />
                  </div>
                  <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{fmtBytes((u?.rxBytes || 0) + (u?.txBytes || 0))}</div>
              </div>
            ))}
          </div>
        </Link>
      )}

      {/* Router / WiFi status — clickable through to Wi-Fi Settings (SSID/password +
          connected devices) only when the router is actually online and reachable.
          Status auto-refreshes (see the polling effect above) but the underlying
          TR-069 check only truly updates every ~5 min for most routers, so that's
          called out explicitly rather than implying it's live-live. A "Sync Now"
          button lets a customer force an immediate check instead of waiting. */}
      {device?.linked && (
        <div className="card" style={{ marginTop: 12, padding: '14px 18px' }}>
          <Link to="/portal/wifi" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, flexShrink: 0,
              background: device.online ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RouterIcon size={18} color={device.online ? '#16a34a' : '#dc2626'} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '.88rem' }}>{device.online ? 'Router Online' : 'Router Offline'}</div>
              {device.online && device.wifiClients != null ? (
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{device.wifiClients} device(s) connected via WiFi</div>
              ) : !device.online ? (
                <div style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>Reconnects automatically once it's back online</div>
              ) : null}
            </div>
            {device.online && <ChevronRight size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />}
          </Link>
          <div style={{
            marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Router status updates automatically every ~5 minutes</span>
            <button
              className="btn btn-secondary btn-sm"
              disabled={syncing}
              onClick={async (e) => {
                e.preventDefault(); e.stopPropagation();
                setSyncing(true);
                try {
                  const { data } = await customerAPI.syncMyDeviceStatus();
                  setDevice(data.data);
                  toast.success(data.data?.online ? 'Router is online' : 'Router did not respond — still offline');
                } catch {
                  toast.error('Sync failed — please try again');
                } finally { setSyncing(false); }
              }}
            >
              <RefreshCw size={12} style={{ marginRight: 5, animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: 12, padding: '16px 20px' }}>
        <div style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
          Quick Actions
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 10 }}>
          {QUICK_ACTIONS.map(a => {
            const tileStyle = {
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '12px 6px',
              textDecoration: 'none', border: '1px solid var(--border)', borderRadius: 12,
            };
            const inner = (
              <>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, background: `${a.color}14`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <a.icon size={16} color={a.color} />
                </div>
                <span style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--text)', textAlign: 'center' }}>{a.label}</span>
              </>
            );
            return a.href ? (
              <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer" style={tileStyle}>{inner}</a>
            ) : (
              <Link key={a.label} to={a.to} style={tileStyle}>{inner}</Link>
            );
          })}
          <SupportQuickAction />
        </div>
      </div>
    </div>
  );
}
